/**
 * MapCanvas - The main React Flow canvas for the mind map
 *
 * Features:
 * - Semantic zoom (different detail levels)
 * - Custom node types
 * - AI-generated connections
 * - Cluster visualization
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  MiniMap,
  useReactFlow,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Type, Image, Link, Sparkles, Brain, Loader2 } from 'lucide-react';

import { useMapStore } from '../../stores/mapStore';
import CustomNode from '../nodes/CustomNode';
import GhostNode from '../nodes/GhostNode';
import ChallengeNode from '../nodes/ChallengeNode';
import SemanticEdge from './SemanticEdge';
import ChallengeEdge from './ChallengeEdge';
import AddNodeModal from './AddNodeModal';
import ClusterBackground from './ClusterBackground';

// Define custom node types
const nodeTypes = {
  custom: CustomNode,
  ghost: GhostNode,
  challenge: ChallengeNode,
};

// Define custom edge types
const edgeTypes = {
  default: SemanticEdge,
  semantic: SemanticEdge,
  challenge: ChallengeEdge,
};

// Zoom level thresholds for semantic zoom
const ZOOM_LEVELS = {
  FAR: 0.4,      // Show only clusters
  MID: 0.7,      // Show main nodes
  CLOSE: 1.0,    // Show all details
};

const MapCanvas = () => {
  const reactFlowRef = useRef(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [addMenuPosition, setAddMenuPosition] = useState({ x: 0, y: 0 });
  const [showAddModal, setShowAddModal] = useState(false);
  const [addNodeType, setAddNodeType] = useState(null);
  const [newNodePosition, setNewNodePosition] = useState({ x: 0, y: 0 });

  const {
    nodes,
    edges,
    viewport,
    isProcessingAI,
    aiStatus,
    clusterColors,
    onNodesChange,
    onEdgesChange,
    onConnect,
    setViewport,
    setSelectedNode,
    updateNodePosition,
    addNode,
    openEditModal,
    settings,
  } = useMapStore();

  const { screenToFlowPosition } = useReactFlow();

  // Calculate semantic zoom level
  const zoomLevel = useMemo(() => {
    if (viewport.zoom < ZOOM_LEVELS.FAR) return 'far';
    if (viewport.zoom < ZOOM_LEVELS.MID) return 'mid';
    return 'close';
  }, [viewport.zoom]);

  // Filter nodes based on zoom level (semantic zoom)
  const visibleNodes = useMemo(() => {
    if (!settings.semanticZoom) return nodes;

    return nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        zoomLevel,
      },
    }));
  }, [nodes, zoomLevel, settings.semanticZoom]);

  // Handle right-click to show add menu
  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    const position = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });
    setAddMenuPosition({ x: event.clientX, y: event.clientY });
    setNewNodePosition(position);
    setShowAddMenu(true);
  }, [screenToFlowPosition]);

  // Handle double-click to quickly add text node
  const handlePaneDoubleClick = useCallback((event) => {
    const position = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });
    setNewNodePosition(position);
    setAddNodeType('text');
    setShowAddModal(true);
  }, [screenToFlowPosition]);

  // Handle add menu selection
  const handleAddMenuSelect = useCallback((type) => {
    setShowAddMenu(false);
    setAddNodeType(type);
    setShowAddModal(true);
  }, []);

  // Handle node position change (for persistence)
  const handleNodeDragStop = useCallback((event, node) => {
    updateNodePosition(node.id, node.position);
  }, [updateNodePosition]);

  // Handle node click
  const handleNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, [setSelectedNode]);

  // Handle node double-click - open edit modal
  const handleNodeDoubleClick = useCallback((event, node) => {
    event.stopPropagation();
    // Don't edit ghost nodes
    if (node.type === 'ghost') return;
    openEditModal(node.id);
  }, [openEditModal]);

  // Handle viewport change
  const handleMove = useCallback((event, viewport) => {
    setViewport(viewport);
  }, [setViewport]);

  // Close add menu when clicking elsewhere
  const handlePaneClick = useCallback(() => {
    setShowAddMenu(false);
  }, []);

  return (
    <div className="relative w-full h-full bg-gray-950">
      <ReactFlow
        ref={reactFlowRef}
        nodes={visibleNodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeDragStop={handleNodeDragStop}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onMove={handleMove}
        onPaneClick={handlePaneClick}
        onPaneContextMenu={handleContextMenu}
        onDoubleClick={handlePaneDoubleClick}
        defaultViewport={viewport}
        minZoom={0.1}
        maxZoom={2}
        fitView
        selectNodesOnDrag={false}
        noDragClassName="nodrag"
        noPanClassName="nopan"
        className="bg-gray-950"
        proOptions={{ hideAttribution: true }}
      >
        {/* Cluster backgrounds */}
        <ClusterBackground nodes={nodes} clusterColors={clusterColors} />

        {/* Background grid */}
        <Background
          variant="dots"
          gap={20}
          size={1}
          color="rgba(99, 102, 241, 0.15)"
        />

        {/* NOTE: React Flow Controls removed - using MapToolbar instead */}

        {/* Mini map - bottom right, above the buttons */}
        <MiniMap
          position="bottom-right"
          nodeColor={(node) => {
            if (node.type === 'challenge') return '#ef4444';
            if (node.type === 'ghost') return '#a855f7';
            return node.data?.clusterColor?.border || '#22c55e';
          }}
          nodeStrokeWidth={2}
          nodeBorderRadius={4}
          maskColor="rgba(0, 0, 0, 0.6)"
          bgColor="#1f2937"
          style={{
            right: 16,
            bottom: 140,
            width: 180,
            height: 120,
            backgroundColor: '#1f2937',
            borderRadius: 8,
            border: '1px solid #374151',
          }}
          pannable
          zoomable
        />

        {/* Zoom level indicator */}
        <Panel position="top-left" className="!m-4">
          <div className="px-3 py-1.5 bg-gray-800/80 backdrop-blur rounded-full text-xs text-gray-400 border border-gray-700">
            {zoomLevel === 'far' && 'Overview Mode'}
            {zoomLevel === 'mid' && 'Normal View'}
            {zoomLevel === 'close' && 'Detail View'}
          </div>
        </Panel>

        {/* Quick add button */}
        <Panel position="bottom-right" className="!m-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setNewNodePosition({ x: viewport.x + 400, y: viewport.y + 300 });
              setAddNodeType('text');
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg shadow-lg shadow-indigo-500/20 transition-colors"
          >
            <Plus size={18} />
            <span>Add Node</span>
          </motion.button>
        </Panel>
      </ReactFlow>

      {/* AI Processing indicator */}
      <AnimatePresence>
        {isProcessingAI && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-3 px-4 py-2.5 bg-gray-800/95 backdrop-blur border border-indigo-500/30 rounded-full shadow-xl"
          >
            <Loader2 size={18} className="text-indigo-400 animate-spin" />
            <span className="text-sm text-gray-300">{aiStatus}</span>
            <Sparkles size={14} className="text-indigo-400" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Context menu for adding nodes */}
      <AnimatePresence>
        {showAddMenu && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            style={{ left: addMenuPosition.x, top: addMenuPosition.y }}
            className="absolute z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden"
          >
            <div className="py-1">
              <button
                onClick={() => handleAddMenuSelect('text')}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-left text-gray-200 hover:bg-gray-700 transition-colors"
              >
                <Type size={16} className="text-blue-400" />
                <span>Add Text Note</span>
              </button>
              <button
                onClick={() => handleAddMenuSelect('image')}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-left text-gray-200 hover:bg-gray-700 transition-colors"
              >
                <Image size={16} className="text-green-400" />
                <span>Add Image</span>
              </button>
              <button
                onClick={() => handleAddMenuSelect('url')}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-left text-gray-200 hover:bg-gray-700 transition-colors"
              >
                <Link size={16} className="text-purple-400" />
                <span>Add URL</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add node modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddNodeModal
            type={addNodeType}
            position={newNodePosition}
            onClose={() => {
              setShowAddModal(false);
              setAddNodeType(null);
            }}
            onSubmit={async (data) => {
              await addNode(addNodeType, data.content, newNodePosition, data);
              setShowAddModal(false);
              setAddNodeType(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MapCanvas;
